// AI Upgraded Service
import { Injectable } from '@angular/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { ComplianceService } from '@client/app/blocks/service/compliance/compliance.service';
import { ActiveFeatureService } from '@client/ng2/services/active-feature/active-feature.service';
import { HistoryHelperService } from '@client/app/blocks/helpers/history.helper.service';
import { MonNumberPipe } from '@monsido/ng2/core/locale/pipes/mon-number.pipe';
import { DatePipe } from '@angular/common';

@Injectable({
    providedIn: 'root',
})
export class ExporterQaParserService {
    constructor (
        private readonly translateService: TranslateService,
        private readonly complianceService: ComplianceService,
        private readonly activeFeatureService: ActiveFeatureService,
        private readonly historyHelperService: HistoryHelperService,
        private readonly monNumberPipe: MonNumberPipe,
        private readonly datePipe: DatePipe,
    ) {}

    parse (data: any[]): any[] {
        if (!data?.length) {
            return [];
        }

        const csv: string[][] = [];
        const allowVerticalAverage = this.activeFeatureService.isFeatureActive('monsido_score');

        csv.push(this.getHeadlines(allowVerticalAverage));

        data.forEach((item) => {
            const entry: any[] = [];
            entry.push(this.datePipe.transform(item.post_processing_done_at ?? item.date, 'MMM d, y, h:mm a'));
            entry.push(item.page_count);
            entry.push(item.documents_count);

            if (item.compliancePercentage != null) {
                entry.push(this.monNumberPipe.transform(item.compliancePercentage / 100, '0.[00]%'));
            } else {
                entry.push(this.getCompliance(item));
            }

            if (allowVerticalAverage) {
                entry.push(item.comparisonToAverage);
            }

            entry.push(item.dead_links_count);
            entry.push(item.dead_images_count);
            entry.push(item.spelling_errors_confirmed_count);
            entry.push(this.historyHelperService.getCaretCharacter(item.changeValueIconClass) + ' ' + item.changePercentageValue);

            csv.push(entry);
        });

        return csv;
    }

    private getHeadlines (allowVerticalAverage: boolean): string[] {
        const headlines = [
            this.translateService.getString('Crawled at'),
            this.translateService.getString('Pages Crawled'),
            this.translateService.getString('Documents Crawled'),
            this.translateService.getString('Domain Compliance'),
        ];

        if (allowVerticalAverage) {
            headlines.push(this.translateService.getString('Comparison to average'));
        }

        headlines.push(
            this.translateService.getString('Broken Links'),
            this.translateService.getString('Broken Images'),
            this.translateService.getString('Misspellings'),
            this.translateService.getString('Changes since Previous Crawl'),
        );

        return headlines;
    }

    private getCompliance (item: any): string {
        item.qaComplianceData ??= this.complianceService.qaHistoryCompliance(item);
        return this.monNumberPipe.transform(item.qaComplianceData?.[0] / 100, '0.[00]%') || '';
    }
}
